import vespidHome from "../assets/vespidHome.svg";
import vulcanPostLogo from "../assets/vulcanPostLogo.svg";
import bfmLogo from "../assets/BFMLogo.svg";
import akpkLogo from "../assets/AKPKLogo.png";
import paynetFintechLogo from "../assets/paynet_fintech.png"
import cradleLogo from "../assets/cradle-logo.svg";
import mdecLogo from "../assets/mdec-logo.svg";
import ctosLogo from "../assets/ctosLogo.svg";
import theedgeLogo from "../assets/theedgeLogo.svg";
import janjilahLogo from "../assets/janjilahLogo.svg";
import kdxLogo from "../assets/kdxLogo.svg";
import mykapitalLogo from "../assets/mykapitalLogo.svg";
import willwaliLogo from "../assets/willwaliLogo.svg";
import brushImage from "../assets/3dicons-brush.png";
import computerImage from "../assets/3dicons-computer.png";
import mobileImage from "../assets/3dicons-mobile.png";
import megaphoneImage from "../assets/3dicons-megaphone.png";
import vespidCuteFinances from '../assets/vespid_cute_finances.png';
import studyInternationalLogo from "../assets/study_international_logo.jpg";
import LazyLoad from 'react-lazyload';
import HexContainer from "../components/HexContainer";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import Button from "../components/Button";
import Sparkline from "../components/Sparkline";
import CircularProgress from "../components/CircularProgress";
import useDeviceTypeHandler from "../hooks/device_type_handler_hook";
import { Instagram, Linkedin } from "lucide-react";
import { IoLogoTiktok } from "react-icons/io5";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.vespid.mobileapp";
const APPLE_STORE_URL = "https://apps.apple.com/us/app/vespid/id6744089122";

// Illustrative growth trend for the sparkline cards (decorative, not literal historical data)
const REGISTERED_USERS_TREND = [400, 550, 700, 950, 1150, 1400, 1650, 1800];
const COMPLETED_LESSONS_TREND = [1200, 2100, 2900, 3800, 4600, 5400, 6000, 6500];

const Home = () => {
  const deviceType = useDeviceTypeHandler();
  const storeUrl = deviceType === "iphone" ? APPLE_STORE_URL : GOOGLE_PLAY_URL;

  return (
    <>
      <SEO
        title="Vespid - Gamified Financial Literacy Education Malaysia | Personal Finance Learning App"
        description="Learn personal finance with Vespid, Malaysia's leading gamified financial literacy education platform. Master budgeting, investing, and money management through interactive lessons and quizzes. Join 2000+ users building their financial future."
        keywords="financial literacy Malaysia, personal finance education, budgeting app Malaysia, financial learning platform, money management education, financial literacy app, investment education Malaysia, financial planning Malaysia, gamified learning finance"
        url="/"
      />
      <StructuredData type="website" />
      <StructuredData type="organization" />
      <StructuredData type="mobileApplication" />
      <div className="max-w-screen-xl lg:mx-auto font-primaryFont font-light text-gray-800 px-8 md:px-0">
      <section className="mt-10 md:mt-0 md:min-h-screen flex flex-col-reverse md:flex-row md:justify-center md:items-center">
        <div className="md:pr-20 md:w-1/2 text-center md:text-left">
          <h1 className="mt-4 font-bold text-3xl md:text-6xl">
            Master Your Money,{" "}
            <span className="text-primaryYellow underline">Level Up</span> Your Life.
          </h1>
          <h2 className="text-xl mt-6 ">
            Vespid is a gamified personal finance learning mobile app platform. Build wealth, one step at a time.
          </h2>
          <div className="flex flex-row my-6 justify-center align-middle md:justify-start gap-4">
            <Button
              href={storeUrl}
              target="_blank"
              variant="primary"
              type="hero"
              size="lg"
            >
              Download Vespid
            </Button>
          </div>
        </div>
        <div className="flex justify-center md:w-4/12">
          <LazyLoad>
            <img
              src={vespidHome}
              alt="Vespid iPhone design"
              loading="lazy"
              className="w-96 md:object-cover"
            />
          </LazyLoad>
        </div>
      </section>

      <section className="mt-24 md:min-h-screen">
        <h2 className="text-4xl font-bold text-center">Gamified for your business</h2>
        <p className="text-center mt-4 text-xl mx-auto max-w-4xl">Transform customer engagement with our proven gamified financial education platform. Choose from flexible solutions that integrate seamlessly with your business goals.</p>
        <div className="justify-items-center lg:flex lg:ml-48">
          <HexContainer title="Native app" subtitle="Personalized and gamified." image={mobileImage} />
          <HexContainer title="Sponsorship" subtitle="Reach engaged learners." image={megaphoneImage} className="lg:-left-16 lg:top-28" />
          <HexContainer title="White Label" subtitle="White label our gamified solutions to your needs." image={brushImage} className="lg:-left-32" />
          <HexContainer title="App Integration" subtitle="Seamlessly embed on your platform." image={computerImage} className="lg:-left-48 lg:top-28" />
        </div>
        <div className="justify-self-center">
          <Button
            to="/solutions"
            variant="secondary"
            type="standard"
            size="md"
            className="md:mt-32 mx-6"
          >
          Our Solutions
        </Button>
        </div>
        
        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center">Featured on</h2>
          <div className="py-4 mt-4">
            <div className="flex flex-wrap gap-x-10 gap-y-8 items-center justify-center px-8">
              <a href="https://vulcanpost.com/874569/vespid-malaysia-financial-literacy-education-app/" target="_blank" rel="noreferrer noopener">
                <img src={vulcanPostLogo} alt="Featured on Vulcan Post" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
              </a>
              <a href="https://www.bfm.my/content/podcast/the-duolingo-for-personal-finance-inside-vespids-pivot" target="_blank" rel="noreferrer noopener">
                <img src={bfmLogo} alt="BFM Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
              </a>
              <a href="https://theedgemalaysia.com/node/812458" target="_blank" rel="noreferrer noopener">
                <img src={theedgeLogo} alt="The Edge Malaysia Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
              </a>
              <a href="https://studyinternational.com/news/student-entrepreneur-full-time-founder-malaysian-entrepreneur/" target="_blank" rel="noreferrer noopener">
                <img src={studyInternationalLogo} alt="Study International Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center">Supporters &amp; Partners</h2>
          <div className="py-4 mt-4">
            <div className="flex flex-wrap gap-x-10 gap-y-8 items-center justify-center px-8">
              <a href="https://www.akpk.org.my/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={akpkLogo} alt="AKPK Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://fintechhub.paynet.my/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={paynetFintechLogo} alt="Paynet Fintech Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://www.cradle.com.my/cip-spark/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={cradleLogo} alt="Cradle Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://www.mdec.my/md-programmes/malaysia-digital-status" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={mdecLogo} alt="MDEC Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://ctoscredit.com.my/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={ctosLogo} alt="CTOS Logo" className="h-12 md:h-14 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://janjilah.com/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={janjilahLogo} alt="Janjilah Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://kdx.com.my/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={kdxLogo} alt="KDX Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://mykapital.my/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={mykapitalLogo} alt="MyKapital Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
              <a href="https://www.willwali.com/" target="_blank" rel="noreferrer noopener">
                <LazyLoad>
                  <img src={willwaliLogo} alt="Willwali Logo" className="h-10 md:h-12 w-auto object-contain flex-shrink-0" />
                </LazyLoad>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-24 justify-items-center">
        <div className="text-center">
          <h2 className="text-4xl font-bold inline-block pb-2">Community Achievements</h2>
        </div>
        <div className="md:flex md:space-x-8 mt-8">
          <div className="p-6 w-72 text-center">
            <Sparkline data={REGISTERED_USERS_TREND} color="#3B82F6" className="mx-auto" />
            <p className="text-5xl font-bold mt-2">2,700+</p>
            <p className="text-sm font-semibold uppercase tracking-wide mt-2 text-gray-700">Registered Users</p>
          </div>
          <div className="p-6 w-72 text-center mt-8 md:mt-0">
            <Sparkline data={COMPLETED_LESSONS_TREND} color="#F2994A" className="mx-auto" />
            <p className="text-5xl font-bold mt-2">6500+</p>
            <p className="text-sm font-semibold uppercase tracking-wide mt-2 text-gray-700">Completed Lessons</p>
          </div>
          <div className="p-6 w-72 text-center mt-8 md:mt-0 flex flex-col items-center">
            <CircularProgress percentage={85} progressColor="#F4B618" trackColor="#09090B">
              <span className="text-3xl font-bold">85%</span>
            </CircularProgress>
            <p className="text-sm font-semibold uppercase tracking-wide mt-4 text-gray-700">Completion Rate</p>
          </div>
        </div>
      </section>
      <section className="mt-8 md:mt-24 px-10 md:px-40">
        <div className="justify-items-center md:flex md:items-center">
          <img
            src={vespidCuteFinances}
            alt="Manage your finances"
            loading="lazy"
            className="w-64 md:object-cover"
          />
          <div className="md:pl-20">
            <h2 className="text-4xl font-bold text-center md:text-left">Join our community</h2>
            <p className="mt-4 text-xl">Be part of the Vespid hive! Connect with us on social media for community
              challenges and exclusive updates. Let's grow our financial knowledge together!
            </p>
            <div className="mt-4 flex space-x-4">
              <a
                href="https://www.instagram.com/vespid_my/"
                target="_blank"
                rel="noreferrer noopener"
              >
                <Instagram className="w-12 h-12 inline" />
              </a>
              <a href="https://www.linkedin.com/company/vespid" target="_blank" rel="noreferrer noopener">
                <Linkedin className="w-12 h-12 inline" />
              </a>
              <a href="https://www.tiktok.com/@vespid_my" target="_blank" rel="noreferrer noopener">
                <IoLogoTiktok className="w-12 h-12 inline" />
              </a>
            </div>
          </div>



        </div>

      </section>
    </div>
    </>
  );
};

export default Home;
